import { Divider } from '@eventbrite/legacy-eds.divider';
import {
    getExperiment,
    initializeSdk,
    isStatsigInitialized,
} from '@eventbrite/legacy-infra.statsig';
import {
    Experiment,
    Chatbot,
    ChatbotContext,
    ConciergeScreen,
} from '@eventbrite/consumer-search.chatbot';
import { Typography } from '@eventbrite/marmalade';
import classNames from 'classnames';
import React, { useCallback, useContext, useEffect } from 'react';
import { LocationInput } from '../../../containers/LocationInput';
import { SearchInput } from '../../../containers/SearchInput';
import { SearchDropdownContext, useLocationContext, useSearchInputContext } from '../../../context';
import { useRunSearch } from '../../../hooks';
import { DropdownOverlay } from '../DropdownOverlay';
import { SearchButton } from '../SearchButton';
import { SearchDropdown } from '../SearchDropdown';
import styles from './SearchBar.module.scss';
import { useUserContext } from '../../../context/User';
import { serializeLocationForCookie } from '../../../utils/locationCookie';
import { ensureSDKIsInitialized } from '@eventbrite/legacy-infra.statsig/utils/utils';
import { CamelCaseLocation, LocationWithMessageDescriptors } from '../../../types/location';

export const isMobile = () => {
    if (typeof window !== 'undefined') {
        return window?.innerWidth <= 480;
    }
    return false;
};

const SearchInputContainer = ({
    handleSearchButtonClick,
}: {
    handleSearchButtonClick: (e: React.MouseEvent<HTMLElement>) => void;
}) => {
    const { showDropdown, setShowDropdown, setShowOverlay } = useContext(SearchDropdownContext);
    const { enabled, setShowChat, showChat } = useContext(ChatbotContext);
    const { location } = useLocationContext();
    const { user } = useUserContext();

    const handleSearchClick = useCallback(() => {
        if (enabled) {
            setShowChat(true);
        } else {
            setShowDropdown(true);
            setShowOverlay(true);
        }
    }, [setShowDropdown, setShowOverlay, setShowChat, enabled]);

    const handleSearchClickOutside = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
            const target = e.target as HTMLElement;
            const elementId = target?.id;
            switch (elementId) {
                case 'search-autocomplete-input':
                    // No-op; The user has clicked into the search input a subsequent time.
                    // See: #handleSearchClick
                    return;

                case 'location-autocomplete':
                case 'search-dropdown-overlay':
                    setShowDropdown(false);
                    return;

                default:
                    setShowDropdown(false);
                    setShowOverlay(false);
                    return;
            }
        },
        [setShowDropdown, setShowOverlay],
    );

    return (
        <div
            className={styles.searchLabelContainer}
            data-heap-id="seo-global-nav-search-bar-search-bar-click"
        >
            <div className={styles.searchLabel}>
                <SearchInput
                    variant="header"
                    dropdownVisible={showDropdown}
                    handleSearchClick={handleSearchClick}
                    handleBackButtonClick={handleSearchClickOutside}
                />
            </div>
            <div className={styles.mobileSearchBarRightSide} data-testid="search-button-mobile">
                <SearchButton onClick={handleSearchButtonClick} />
            </div>
            {showDropdown && <SearchDropdown onClickOutside={handleSearchClickOutside} />}
            {showChat && (
                <ConciergeScreen
                    onClose={() => setShowChat(false)}
                    user={user}
                    location={serializeLocationForCookie(
                        location as LocationWithMessageDescriptors,
                    )}
                />
            )}
        </div>
    );
};

const LocationInputContainer = () => {
    const { setShowOverlay } = useContext(SearchDropdownContext);

    const handleLocationClick = useCallback(() => {
        setShowOverlay(true);
    }, [setShowOverlay]);

    const handleLocationSelect = () => {
        if (window?.innerWidth >= 660) {
            setShowOverlay(false);
        }
    };

    return (
        <div className={styles.dividerAndLocationContainer}>
            <div className={styles.dividerContainer}>
                <Divider direction="vertical" color="ui-500" />
            </div>
            <div className={styles.locationContainer} onClick={handleLocationClick}>
                <div className={styles.locationLabel}>
                    <LocationInput onLocationSelect={handleLocationSelect} variant="header" />
                </div>
            </div>
        </div>
    );
};

export const SearchBar = ({
    searchButtonClickedExtraHandler,
}: {
    searchButtonClickedExtraHandler?: (e?: React.MouseEvent<HTMLElement>) => void;
}) => {
    const { showOverlay, setShowOverlay } = useContext(SearchDropdownContext);
    const { query } = useSearchInputContext();
    const handleSearch = useRunSearch();

    const onOverlayClose = useCallback(() => {
        setShowOverlay(false);
    }, [setShowOverlay]);

    const handleSearchButtonClick = useCallback(
        (e?: React.MouseEvent<HTMLElement>) => {
            try {
                searchButtonClickedExtraHandler?.(e);
            } catch (_e) {
                console.error('Handle search button failed.');
            }
            handleSearch({
                search: { q: query || '' },
                analytics: { action: 'RunSearch', label: query },
            });
        },
        [handleSearch, query],
    );

    const searchBarClassnames = classNames(styles.searchBarWithLocation, {
        [styles.overlayOpen || '']: showOverlay,
    });

    return (
        <>
            <div className={searchBarClassnames} data-testid="header-search">
                <Typography
                    variant="body-md"
                    as="span"
                    color="neutral-700"
                    className={styles.searchBarContent}
                >
                    <SearchInputContainer handleSearchButtonClick={handleSearchButtonClick} />
                    <LocationInputContainer />
                    <div
                        className={styles.desktopSearchBarRightSide}
                        data-testid="search-button-desktop"
                    >
                        <SearchButton onClick={handleSearchButtonClick} />
                    </div>
                </Typography>
                <DropdownOverlay
                    domNodeId="__next"
                    onClose={onOverlayClose}
                    isVisible={showOverlay}
                />
            </div>
        </>
    );
};
